import produce from 'immer';
import {
  QueueWithAssignmentCounts,
  ReadQueuesApiArg,
  ReadQueuesApiResponse,
  ReadQueuesSortKey,
  readQueuesSortKeys,
} from 'lib-shared-types-api';
import { useEffect, useMemo, useRef } from 'react';
import { useUpdateEffect } from 'react-use';
import { DeepWritable } from 'ts-essentials';

import { queuesApi } from 'apis/queues';
import {
  useLazyPaginatedEndpoint,
  UsePaginatedResult,
} from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { getSortObjectFromString } from 'utils/url/getSortObjectFromString';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useServerStorage } from 'utils/useServerStorage';
import { useCurrentUser } from 'utils/user';

type UseQueueDashboardQueuesResult = UsePaginatedResult<
  QueueWithAssignmentCounts,
  ReadQueuesApiArg,
  ReadQueuesApiResponse
>;

/** The default queues page limit. */
const defaultPageLimit = 50;

/**
 * A hook that handles fetching and updating dashboard queues based on URL
 * params and other conditions.
 */
export const useQueueDashboardQueues = (): UseQueueDashboardQueuesResult => {
  const { user } = useCurrentUser();
  const { params, setParams } = useAppSearchParams('/dashboard/queues');
  const storage = useServerStorage('user');

  // Controls whether we should skip calling `changeArg` on the paginated endpoint, so we don't
  // trigger re-fetches when we shouldn't, such as in response to the arg changing after pagination.
  const skipChangeArg = useRef(false);

  const paramsToApiArg = (): ReadQueuesApiArg =>
    produce<ReadQueuesApiArg>(
      {
        isArchived: params.type === 'archived',
        agentId: params.type === 'agent' ? user.id : undefined,
        queueIds: undefined,
        searchTerm: params.q,
        limit: params.limit ? parseInt(params.limit) : defaultPageLimit,
        offset: params.offset ? parseInt(params.offset) : undefined,
      },
      (draft) => {
        const sort = getSortObjectFromString<ReadQueuesSortKey>(
          params.sort_by ?? 'name:asc',
          readQueuesSortKeys,
        )!;

        // If we're sorting by name, we leave it as is, otherwise we sort by the requested sort key
        // and then by name in ascending order.
        draft.sort =
          sort.key === 'name'
            ? sort
            : [sort, { key: 'name', direction: 'asc' }];

        if (params.type === 'favourite') {
          draft.queueIds = storage.data.dashboard
            ?.favoriteQueueIds as DeepWritable<string[]>;
        } else if (params.type === 'custom') {
          draft.queueIds = params.queue ? [params.queue].flat() : undefined;
        }
      },
    );

  const result = useLazyPaginatedEndpoint({
    endpoint: queuesApi.endpoints.readQueues,
    itemsSelector: ({ data: { queues } }) => queues,
  });

  // Handles changes to the URL search params, and updates the endpoint arg accordingly, triggering
  // a refetch in most cases. In the case of favorite queues, where the user has no favorites, we
  // skip fetching and instead show an empty state immediately.
  useEffect(() => {
    if (skipChangeArg.current) {
      skipChangeArg.current = false;
    } else {
      const arg = paramsToApiArg();
      const skipFetch = params.type === 'favourite' && !arg.queueIds?.length;

      result.changeArg(arg, { skipFetch });
    }

    // We only care when params change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Handles updating the URL search params whenever the arg limit and offset changes.
  useUpdateEffect(() => {
    const arg = paramsToApiArg();

    if (result.arg?.limit !== arg.limit || result.arg?.offset !== arg.offset) {
      setParams({
        limit: result.arg?.limit?.toString(),
        offset: result.arg?.offset?.toString(),
      });

      // We don't want to re-fetch through changeArg to avoid recursive-ness.
      skipChangeArg.current = true;
    }

    // Only run this when the pagination args change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.arg?.limit, result.arg?.offset]);

  return <UseQueueDashboardQueuesResult>useMemo(
    () => ({
      ...result,

      // If the user has no favorite queues, we want to show an empty state immediately.
      ...(params.type === 'favourite' &&
        !result.arg?.queueIds?.length && {
          asyncStatus: 'fulfilled',
          items: [],
        }),
    }),
    // We only care when the paginated API result changes, as a result of the useEffect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [result.asyncStatus, result.arg],
  );
};
